import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import BhtLogo from "@/components/common/BhtLogo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email || !consent || loading) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          first_name: firstName || null,
          audience: "hospitality",
          lang,
          source: "website_footer",
          marketing_consent: true,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      if (data.status === "already_subscribed") {
        toast.success(lang === "nl" ? "U bent al aangemeld — bedankt." : "You're already subscribed — thank you.");
      } else {
        toast.success(
          lang === "nl"
            ? "Bijna klaar. Kijk in uw inbox en bevestig met één klik."
            : "Almost done. Please check your inbox and confirm with one click."
        );
      }
      setDone(true);
      setEmail("");
      setFirstName("");
      setConsent(false);
    } catch (err) {
      toast.error(lang === "nl" ? "Er ging iets mis. Probeer het opnieuw." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      heading: t("footer.product"),
      links: [
        { label: t("nav.product"), to: `/${lang}/bht-one` },
        { label: t("nav.platform"), to: `/${lang}/platform` },
        { label: t("nav.why"), to: `/${lang}/why-bht-one` },
        { label: t("nav.modules"), to: `/${lang}/modules` },
        { label: lang === "nl" ? "Prijzen" : "Pricing", to: `/${lang}/pricing` },
      ],
    },
    {
      heading: t("footer.modules"),
      links: [
        { label: "Reception", to: `/${lang}/modules/reception` },
        { label: "Housekeeping", to: `/${lang}/modules/housekeeping` },
        { label: "Restaurant", to: `/${lang}/modules/restaurant` },
        { label: "AI Copilot", to: `/${lang}/modules/ai-copilot` },
        { label: "Analytics", to: `/${lang}/modules/analytics` },
      ],
    },
    {
      heading: t("footer.solutions"),
      links: [
        { label: "Boutique hotels", to: `/${lang}/solutions/boutique-hotels` },
        { label: "Hotel chains", to: `/${lang}/solutions/hotel-chains` },
        { label: "Resorts", to: `/${lang}/solutions/resorts` },
        { label: "Family hotels", to: `/${lang}/solutions/family-hotels` },
        { label: "Wellness hotels", to: `/${lang}/solutions/wellness-hotels` },
        { label: "Holiday parks", to: `/${lang}/industries/holiday-parks` },
      ],
    },
    {
      heading: t("footer.resources"),
      links: [
        { label: t("nav.knowledge"), to: `/${lang}/knowledge` },
        { label: lang === "nl" ? "Case studies" : "Case studies", to: `/${lang}/case-studies` },
        { label: lang === "nl" ? "Manifesto" : "Manifesto", to: `/${lang}/manifesto` },
        { label: lang === "nl" ? "Community" : "Community", to: `/${lang}/community` },
        { label: "Documentation", to: `/${lang}/knowledge` },
      ],
    },
    {
      heading: t("footer.company"),
      links: [
        { label: lang === "nl" ? "Bedrijf" : "Company", to: `/${lang}/company` },
        { label: lang === "nl" ? "Werken bij BHT" : "Careers", to: `/${lang}/careers` },
        { label: lang === "nl" ? "Media & press" : "Media & press", to: `/${lang}/media` },
        { label: lang === "nl" ? "Investeerders" : "Investors", to: `/${lang}/investors` },
        { label: lang === "nl" ? "Partners" : "Partners", to: `/${lang}/partners` },
        { label: "Contact", to: `/${lang}/contact` },
      ],
    },
    {
      heading: t("footer.trust"),
      links: [
        { label: "Security", to: `/${lang}/trust/security` },
        { label: "Privacy", to: `/${lang}/trust/privacy` },
        { label: "Responsible AI", to: `/${lang}/trust/responsible-ai` },
        { label: "GDPR", to: `/${lang}/trust/privacy` },
        { label: "Compliance", to: `/${lang}/trust/compliance` },
      ],
    },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-white/[0.06] bg-bht-void" data-testid="site-footer">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[1px] w-[70%] -translate-x-1/2 bg-gradient-to-r from-transparent via-bht-gold/50 to-transparent" />

      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        {/* Newsletter */}
        <div className="mb-20 grid gap-10 border-b border-white/[0.06] pb-16 md:grid-cols-2 md:items-end">
          <div>
            <div className="eyebrow">signal / no noise</div>
            <h2 className="mt-5 max-w-lg font-serif text-4xl leading-[1.1] tracking-tight sm:text-5xl">
              {t("footer.newsletter_h")}
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/60">{t("footer.newsletter_sub")}</p>
          </div>
          <form
            onSubmit={submitNewsletter}
            className="md:justify-self-end w-full max-w-md space-y-3"
            data-testid="footer-newsletter-form"
            aria-label={t("footer.newsletter_placeholder")}
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_1.4fr]">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={lang === "nl" ? "Voornaam (optioneel)" : "First name (optional)"}
                className="rounded-full border border-white/10 bg-white/[0.02] px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-bht-gold/60 focus:outline-none"
                data-testid="footer-newsletter-firstname"
                autoComplete="given-name"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletter_placeholder")}
                className="rounded-full border border-white/10 bg-white/[0.02] px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-bht-gold/60 focus:outline-none"
                data-testid="footer-newsletter-input"
                autoComplete="email"
              />
            </div>
            <label className="flex items-start gap-3 text-xs leading-relaxed text-white/55" data-testid="footer-newsletter-consent-label">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-bht-gold"
                data-testid="footer-newsletter-consent"
              />
              <span>
                {lang === "nl"
                  ? "Ik geef toestemming voor het ontvangen van de maandelijkse BHT-nieuwsbrief. U kunt zich op elk moment uitschrijven."
                  : "I consent to receive the monthly BHT newsletter. You can unsubscribe at any time."}
              </span>
            </label>
            <div className="flex items-center justify-between gap-4">
              <p className="text-[11px] text-white/40">
                {lang === "nl" ? "Dubbele opt-in. Geen spam. Ooit." : "Double opt-in. No spam. Ever."}
              </p>
              <button
                type="submit"
                disabled={loading || done || !email || !consent}
                className="rounded-full bg-bht-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-bht-void transition-transform hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                data-testid="footer-newsletter-submit"
              >
                {loading
                  ? (lang === "nl" ? "Versturen…" : "Sending…")
                  : done
                    ? (lang === "nl" ? "Check inbox" : "Check inbox")
                    : t("footer.newsletter_cta")}
              </button>
            </div>
          </form>
        </div>

        {/* Columns */}
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_repeat(6,1fr)]">
          <div>
            <Link to={`/${lang}`} className="inline-flex items-center gap-3" data-testid="footer-logo">
              <BhtLogo className="h-9 w-9" />
              <div className="leading-none">
                <div className="font-serif text-xl tracking-tight">BHT Solutions</div>
                <div className="eyebrow mt-1 text-[9px]">{lang === "nl" ? "De digitale hotelmanager" : "The digital hotel manager"}</div>
              </div>
            </Link>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/55">
              {lang === "nl"
                ? "Gemaakt door hoteliers. Werkt naast uw bestaande PMS. Voor familiehotels én hotelgroepen."
                : "Built by hoteliers. Works alongside your existing PMS. For family-run hotels and hotel groups alike."}
            </p>
          </div>

          {cols.map((c) => (
            <div key={c.heading}>
              <div className="eyebrow mb-4">{c.heading}</div>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={`${c.heading}-${l.label}`}>
                    <Link
                      to={l.to}
                      className="text-[13px] text-white/60 transition-colors hover:text-bht-gold"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-20 flex flex-col items-start justify-between gap-4 border-t border-white/[0.06] pt-8 text-xs text-white/40 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <span>© {year} BHT Solutions.</span>
            <span>{t("footer.rights")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>{t("footer.made")}</span>
            <span className="h-1 w-1 rounded-full bg-bht-gold/80" />
            <span className="font-mono uppercase tracking-widest">v 1.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
